import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router";
import { ChevronRight, GraduationCap, PackageOpen, SearchX } from "lucide-react";
import {
  Button,
  Card,
  EmptyState,
  FilterBar,
  PageHeader,
  Progress,
  Skeleton,
} from "@codelittinc/carbon-design-system";
import type { MyToolsResponse } from "@carbon-os/server/contract";
import {
  ACTION,
  ICON,
  NotConnected,
  SignInAgain,
  NotInGatekeeper,
  Unavailable,
  useRetryFocus,
  WrongAccount,
  type FocusRef,
} from "../components/Panels";
import { TicketLink } from "../components/TicketLink";
import { ToolCard } from "../components/ToolCard";
import { trackApp, trackToolClick, trackTraining } from "../lib/analytics";
import type { MyToolsState } from "../lib/api";
import { useSettled } from "../lib/settle";
import { FILTER_THRESHOLD, filterTools, isHttpUrl } from "../lib/tools";
import { firstUnfinished, hasStarted, overall, trackEnded, type TrainingState } from "../lib/training";

export function Home({
  state,
  retry,
  training,
}: {
  state: MyToolsState;
  retry: () => void;
  /** Only ever adds the nudge: no training state can turn My tools into an error. */
  training: TrainingState;
}) {
  const { focusRef, onRetry } = useRetryFocus(state, retry);

  switch (state.kind) {
    case "loading":
      return <Loading />;
    case "ready":
      return <Ready data={state.data} training={training} focusRef={focusRef} />;
    case "unavailable": {
      // No ticket button: its URL arrives with the tool list, which this state
      // never received. The sentence still points people to IT.
      const again = state.failures > 1;
      return (
        <Unavailable
          title="We can't reach your tool list right now"
          description={`Your access hasn't changed. This is a connection problem on our side. Try again in a moment.${again ? " If this keeps happening, raise a ticket with IT." : ""}`}
          retrying={state.retrying}
          retry={onRetry}
          focusRef={focusRef}
        />
      );
    }
    case "signin_failed":
      return <SignInAgain focusRef={focusRef} />;
    case "wrong_account":
      return <WrongAccount signedInAs={state.signedInAs} signOutUrl={state.signOutUrl} focusRef={focusRef} />;
    case "not_in_gatekeeper":
      return (
        <NotInGatekeeper
          signedInAs={state.signedInAs}
          signOutUrl={state.signOutUrl}
          accessRequestUrl={state.accessRequestUrl}
          focusRef={focusRef}
        />
      );
    case "not_connected":
      return <NotConnected message={state.message} focusRef={focusRef} />;
  }
}

function Loading() {
  return (
    <div>
      <p role="status" className="sr-only">
        Loading your tools
      </p>
      <div className="mb-6">
        <Skeleton className="h-8 w-64 max-w-full motion-reduce:animate-none" />
        <Skeleton className="mt-2 h-4 w-48 max-w-full motion-reduce:animate-none" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-[72px] rounded-lg motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  );
}

function greeting(now: Date, name: string): string {
  const hour = now.getHours();
  const part = hour >= 5 && hour < 12 ? "morning" : hour >= 12 && hour < 18 ? "afternoon" : "evening";
  const firstName = name.trim().split(/\s+/)[0];
  return firstName ? `Good ${part}, ${firstName}` : `Good ${part}`;
}

function countSentence(count: number): string {
  return count === 1
    ? "You have 1 tool. Select it to open it in a new tab."
    : `You have ${count} tools. Pick one to open it in a new tab.`;
}

function Greeting({ name, description, focusRef }: { name: string; description: string; focusRef: FocusRef }) {
  return (
    <PageHeader
      title={
        <span ref={focusRef} tabIndex={-1} className="outline-none">
          {greeting(new Date(), name)}
        </span>
      }
      description={description}
    />
  );
}

/**
 * How many open trainings are assigned and how far along they are, one click from
 * the first unfinished track in Gatekeeper's order. "Start" until anything has
 * been done. Nothing at all while training loads, fails, or is all done.
 */
function TrainingNudge({ training }: { training: TrainingState }) {
  if (training.kind !== "ready") return null;
  const track = firstUnfinished(training.tracks, training.done);
  if (!track) return null;
  // An ended track can take no more work, so it is left out of the count and the bar.
  const open = training.tracks.filter((t) => !trackEnded(t));
  const { done, total } = overall(open, training.done);
  const count = open.length;
  const title = hasStarted(open, training.done) ? "Continue your training" : "Start your training";

  return (
    <Card
      asChild
      hoverable
      className="group mb-6 flex min-h-[72px] items-center gap-3 focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:outline-none motion-reduce:transition-none"
    >
      <Link
        to={`/training?track=${encodeURIComponent(track.id)}`}
        onClick={() => trackTraining("training_nudge_clicked", { track_id: track.id })}
      >
        <GraduationCap aria-hidden className={`${ICON} shrink-0 text-accent-text`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">{title}</p>
          <p className="truncate text-xs text-text-secondary">
            {count === 1 ? "1 training assigned" : `${count} trainings assigned`} · {done} of {total} done
          </p>
          <Progress value={done} max={total} aria-label="Training progress" className="mt-2" />
        </div>
        <ChevronRight
          aria-hidden
          className="size-4 shrink-0 text-text-secondary group-hover:text-accent-text"
        />
      </Link>
    </Card>
  );
}

function Ready({
  data,
  training,
  focusRef,
}: {
  data: MyToolsResponse;
  training: TrainingState;
  focusRef: FocusRef;
}) {
  // An older server answered 200 with no person; this one refuses with 403.
  if (!data.person) {
    return <NotInGatekeeper signedInAs={data.signedInAs} accessRequestUrl={data.accessRequestUrl} focusRef={focusRef} />;
  }

  if (data.tools.length === 0) {
    return (
      <>
        <Greeting name={data.person.name} description="Welcome to Carbon OS." focusRef={focusRef} />
        <TrainingNudge training={training} />
        <EmptyState
          icon={<PackageOpen className={ICON} />}
          title="Nothing has been set up for you yet"
          description="When IT gives you access to a tool, it'll show up here, ready to open. If you think something's missing, raise a ticket with IT."
          action={
            data.accessRequestUrl ? (
              <TicketLink href={data.accessRequestUrl} variant="default" className={ACTION} via="no_tools" />
            ) : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      <Greeting
        name={data.person.name}
        description={countSentence(data.tools.length)}
        focusRef={focusRef}
      />
      <TrainingNudge training={training} />
      <ToolGrid tools={data.tools} accessRequestUrl={data.accessRequestUrl} />
      {data.accessRequestUrl ? (
        <p className="mt-8 flex flex-wrap items-center gap-x-2 text-sm text-text-secondary">
          Missing a tool?
          <TicketLink href={data.accessRequestUrl} className="h-11 px-0 sm:h-auto" via="missing_tool" />
        </p>
      ) : null}
    </>
  );
}

function ToolGrid({
  tools,
  accessRequestUrl,
}: {
  tools: MyToolsResponse["tools"];
  accessRequestUrl: string | null;
}) {
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const showFilter = tools.length > FILTER_THRESHOLD;
  const visible = showFilter ? filterTools(tools, query) : tools;
  const only = visible.length === 1 ? visible[0] : undefined;
  const openable = only && isHttpUrl(only.url) ? only : undefined;
  // Counted once the typing stops, as how many it left: never the text.
  const settle = useSettled((text) =>
    trackApp("tools_filtered", { visible_count: filterTools(tools, text).length, total_count: tools.length }));
  const changeQuery = (next: string) => {
    setQuery(next);
    settle(next);
  };

  // "/" jumps to the filter from anywhere that isn't already a text field.
  useEffect(() => {
    if (!showFilter) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName ?? "")) {
        return;
      }
      event.preventDefault();
      input.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showFilter]);

  const onFilterKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      changeQuery("");
    } else if (event.key === "Enter" && openable?.url) {
      // Inside the key event, so the browser treats it as the person's action.
      trackToolClick(openable, "filter_enter");
      window.open(openable.url, "_blank", "noopener,noreferrer");
    }
  };

  const clearFilter = () => {
    changeQuery("");
    input.current?.focus();
  };

  return (
    <>
      {showFilter ? (
        <FilterBar
          search={query}
          onSearchChange={changeQuery}
          searchPlaceholder="Filter by name"
          searchLabel="Filter your tools"
          searchInputProps={{
            ref: input,
            onKeyDown: onFilterKey,
            type: "search",
            autoComplete: "off",
          }}
          // The design system stacks it on phones and gives it a 44px touch target.
          size="touch"
        >
          <p role="status" aria-live="polite" className="text-sm text-text-secondary">
            Showing {visible.length} of {tools.length}
            {openable ? ` · Press Enter to open ${openable.name}` : ""}
          </p>
        </FilterBar>
      ) : null}

      {visible.length > 0 ? (
        <ul aria-label="Your tools" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map((tool) => (
            <li key={tool.key} className="min-w-0">
              <ToolCard tool={tool} accessRequestUrl={accessRequestUrl} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<SearchX className={ICON} />}
          title={`No tools match "${query}"`}
          description={`Check the spelling, or clear the filter to see all ${tools.length}.`}
          action={
            <Button variant="outline" size="lg" className={ACTION} onClick={clearFilter}>
              Clear filter
            </Button>
          }
        />
      )}
    </>
  );
}
